import { useEffect, useId, useMemo, useRef, useState } from "react";
import Chevron from "../icons/Chevron";
import clsx from "clsx";
import GradientWrapper from "./misc/Gradient";
import { filterBySearch } from "@/lib/search";

type DropdownProps = {
    options: { label: string; value: number }[];
    value: number | null;
    placeholder?: string;
    openTop?: boolean;
    onChange: (value: number) => void;
    className?: string;
};

export default function Dropdown({
    options,
    value,
    placeholder = "Bitte wählen ...",
    openTop,
    onChange,
    className,
}: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listId = useId();

    const selectedLabel = useMemo(() => {
        const found = options.find((o) => o.value === value);
        return found?.label ?? placeholder;
    }, [options, value, placeholder]);

    const filteredOptions = useMemo(
        () => filterBySearch(options, searchQuery, (option) => option.label),
        [options, searchQuery]
    );

    useEffect(() => {
        if (!isOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear the search when the dropdown closes
            setSearchQuery("");
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            setIsOpen(false);
            triggerRef.current?.focus();
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    const handleClick = (opt: { label: string; value: number }) => {
        onChange(opt.value);
        setIsOpen(false);
    };

    return (
        <div ref={rootRef} className={clsx("z-40 relative flex flex-col w-full text-green-800", className)}>
            <button
                ref={triggerRef}
                type="button"
                aria-expanded={isOpen}
                aria-controls={listId}
                onClick={() => setIsOpen((prev) => !prev)}
                className={clsx(
                    "relative flex justify-between items-center gap-2 bg-white px-3 py-2 border-2 border-white w-full min-h-11 overflow-hidden transition-all duration-300 cursor-pointer",
                    "focus-visible:outline-none focus-visible:border-button-focus-border",
                    isOpen ? (openTop ? "rounded-b-2xl" : "rounded-t-2xl rounded-b-none") : "rounded-2xl"
                )}
            >
                {/* Closed: own gradient. Open: cross-fades to the first part of the gradient the list continues
                    (flowing away from the trigger, so flipped for openTop) */}
                <GradientWrapper
                    gradient={clsx("bg-dropdown-item transition-opacity duration-300", isOpen && "opacity-0")}
                />
                <GradientWrapper
                    gradient={clsx(
                        "transition-opacity duration-300 [background:var(--gradient-dropdown-open-trigger)]",
                        openTop && "-scale-y-100",
                        !isOpen && "opacity-0"
                    )}
                />
                <span className="relative font-16-bold text-left">{selectedLabel}</span>
                <span
                    aria-hidden
                    className={clsx(
                        "relative text-green-800 transition-transform duration-300 shrink-0",
                        isOpen ? "rotate-90" : "-rotate-90"
                    )}
                >
                    <Chevron width="16" height="16" />
                </span>
            </button>
            <div
                id={listId}
                inert={!isOpen}
                className={clsx(
                    "absolute flex flex-col bg-white border-2 border-white w-full overflow-hidden transition-all duration-300 ease-out",
                    openTop ? "bottom-full rounded-t-2xl border-b-0" : "top-full rounded-b-2xl border-t-0",
                    isOpen ? "max-h-60 opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-2"
                )}
            >
                {/* One gradient for the whole list, continuing the open trigger's; search and options stay transparent above it */}
                <GradientWrapper
                    gradient={clsx("[background:var(--gradient-dropdown-open-list)]", openTop && "-scale-y-100")}
                />
                <div className="relative border-white border-b-2">
                    <input
                        type="text"
                        aria-label="Suchen"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Suchen..."
                        className="z-10 relative bg-transparent px-3 py-2 outline-none w-full font-16-medium text-green-800 placeholder:text-teal-500"
                    />
                </div>

                <div className="relative max-h-48 overflow-y-auto dropdown-scrollbar">
                    {filteredOptions.length === 0 ? (
                        <p className="px-3 py-2 font-12-medium text-teal-500">Keine Treffer</p>
                    ) : (
                        filteredOptions.map((option, index) => {
                            const isLast = index === filteredOptions.length - 1;
                            const isSelected = option.value === value;
                            return (
                                <button
                                    key={String(option.value)}
                                    type="button"
                                    aria-current={isSelected || undefined}
                                    onClick={() => handleClick(option)}
                                    className={clsx(
                                        "group relative hover:bg-quaternary/25 active:bg-quaternary/25 px-3 py-2 border-white border-b-2 w-full text-left cursor-pointer",
                                        "focus-visible:outline-none focus-visible:bg-quaternary/25",
                                        isSelected && "bg-greenShade-15",
                                        isLast && "border-b-0"
                                    )}
                                >
                                    {option.label}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
